/**
 * Vendor bill / invoice calculator: given a service amount + VAT-flag + TDS
 * category, computes the full breakdown of payments to vendor and government.
 *
 * @example
 * calculateBill({
 *   serviceAmount: 100_000,
 *   applyVAT: true,
 *   tdsType: "service-vat-registered",
 * });
 * // {
 * //   serviceAmount: 100000,
 * //   vat: 13000,
 * //   tds: 1500,           // 1.5% of service amount (not VAT)
 * //   grossInvoice: 113000,
 * //   payableToVendor: 111500, // gross − TDS
 * //   payableToGovtVAT: 13000,
 * //   payableToGovtTDS: 1500,
 * // }
 */

import { calculateTDS } from "./tds.js";
import { calculateVAT } from "./vat.js";
import type {
  BillResult,
  FiscalYear,
  TdsCategory,
} from "./types.js";
import { ensureNonNegative, r2 } from "./util.js";

/** Options for `calculateBill`. */
export interface BillOptions {
  /** Service amount before VAT. */
  readonly serviceAmount: number;
  /** Whether VAT applies (default false — vendor not VAT-registered). */
  readonly applyVAT?: boolean;
  /** TDS category (skip if no TDS). */
  readonly tdsType?: Exclude<TdsCategory, "salary">;
  /** Override VAT rate. */
  readonly vatRate?: number;
  /** Fiscal year (default current). */
  readonly fiscalYear?: FiscalYear;
}

/**
 * Compute the full vendor invoice with VAT + TDS.
 *
 * TDS is computed on the **service amount only**, not the VAT (per IRD).
 * VAT is added to the service amount; TDS is deducted from the gross invoice.
 */
export function calculateBill(options: BillOptions): BillResult {
  const {
    serviceAmount,
    applyVAT = false,
    tdsType,
    vatRate,
    fiscalYear,
  } = options;
  ensureNonNegative(serviceAmount, "calculateBill");

  let vat = 0;
  let grossInvoice = serviceAmount;
  if (applyVAT) {
    const v = calculateVAT(serviceAmount, {
      fiscalYear,
      ...(vatRate !== undefined && { rate: vatRate }),
    });
    vat = v.vat;
    grossInvoice = v.total;
  }

  let tds = 0;
  if (tdsType) {
    const t = calculateTDS({ amount: serviceAmount, type: tdsType, fiscalYear });
    tds = t.tds;
  }

  return {
    serviceAmount: r2(serviceAmount),
    vat: r2(vat),
    tds: r2(tds),
    grossInvoice: r2(grossInvoice),
    payableToVendor: r2(grossInvoice - tds),
    payableToGovtVAT: r2(vat),
    payableToGovtTDS: r2(tds),
  };
}
